import { useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../../../Components/Card/Card';
import { Dropdown as DropdownBase } from '../../../Components/FormComponents/Dropdown';
import PackagesTable from '../../../Components/Voss/Packages/PackagesTable';
import { packageStatusConfig, packageStatuses, packageTypes } from '../../../Components/Voss/Packages/packageConstants';
import packageActions from '../../../Actions/Voss/packageActions';
import { useAppDispatch } from '../../../Store/types';
import routes from '../../../Constants/routes';
import { Button, ButtonOption } from '../../../Components/Fundamentals/Button';
import type { RootState } from '../../../Components/Voss/Packages/Packages.types';
import type { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import { VossPackageTypeText, type VossPackageType } from '../../../Constants/VOSS/vossPackageType';

interface DropdownProps {
    options: { id: string | number; name: string }[];
    value: string | number;
    emptyOption: string;
    onChange: (value: string) => void;
}

const Dropdown = DropdownBase as unknown as ComponentType<DropdownProps>;

const PackagesContainer = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const packages = useSelector((state: RootState) => state.PackageReducer.packages);
    const isLoading = useSelector((state: RootState) => state.CommonReducer.dataLoading.packages);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<VossVersionStatus | null>(null);
    const [typeFilter, setTypeFilter] = useState<VossPackageType | null>(null);
    const [targetGroupFilter, setTargetGroupFilter] = useState<string | null>(null);

    useEffect(() => {
        if (packages === undefined) void dispatch(packageActions.getPackages());
    }, [dispatch, packages]);

    const targetGroupOptions = useMemo(() => {
        const byId = new Map<string, string>();
        (packages ?? []).forEach((vossPackage) =>
            (vossPackage.targetGroups ?? []).forEach((group) => {
                if (group.id !== undefined) byId.set(group.id, group.name ?? group.id);
            })
        );

        return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [packages]);

    const filteredPackages = useMemo(() => {
        const term = search.trim().toLowerCase();

        return (packages ?? []).filter((vossPackage) => {
            const matchesSearch = term.length === 0 || (vossPackage.name ?? '').toLowerCase().includes(term);
            const matchesStatus = statusFilter === null || vossPackage.status === statusFilter;
            const matchesType = typeFilter === null || vossPackage.type === typeFilter;
            const matchesTargetGroup = targetGroupFilter === null || (vossPackage.targetGroups ?? []).some((group) => group.id === targetGroupFilter);

            return matchesSearch && matchesStatus && matchesType && matchesTargetGroup;
        });
    }, [packages, search, statusFilter, typeFilter, targetGroupFilter]);

    return (
        <Card>
            <Card.Header>
                <Card.Header.Title>{t('packages.title')}</Card.Header.Title>
                <Card.Header.Buttons>
                    <Button option={ButtonOption.PRIMARY} onClick={() => void navigate(routes.vossPackagesCreate)}>
                        {t('packages.createPackage')}
                    </Button>
                </Card.Header.Buttons>
            </Card.Header>
            <Card.Content isLoading={isLoading}>
                <div className="d-flex align-items-center gap-16 mb-16">
                    <div className="form-group search-group flex-1 mb-0">
                        <input type="text" className="form-control" placeholder={t('packages.searchByName') ?? ''} value={search} onChange={(e) => setSearch(e.target.value)} />
                        <span className="search-icon" />
                        <button
                            type="button"
                            className={`clear-search border-0 bg-transparent ${search.length > 0 ? 'show' : ''}`}
                            aria-label={t('common.clear') ?? ''}
                            onClick={() => setSearch('')}
                        />
                    </div>
                    <div className="form-group mb-0">
                        <Dropdown
                            emptyOption={t('packages.allStatuses')}
                            options={packageStatuses.map((status) => ({ id: status, name: t(packageStatusConfig[status].labelKey) }))}
                            value={statusFilter ?? ''}
                            onChange={(value) => setStatusFilter(value === '' ? null : (Number(value) as VossVersionStatus))}
                        />
                    </div>
                    <div className="form-group mb-0">
                        <Dropdown
                            emptyOption={t('packages.allTypes')}
                            options={packageTypes.map((packageType) => ({ id: packageType, name: VossPackageTypeText[packageType] }))}
                            value={typeFilter ?? ''}
                            onChange={(value) => setTypeFilter(value === '' ? null : (Number(value) as VossPackageType))}
                        />
                    </div>
                    <div className="form-group mb-0">
                        <Dropdown
                            emptyOption={t('packages.allTargetGroups')}
                            options={targetGroupOptions}
                            value={targetGroupFilter ?? ''}
                            onChange={(value) => setTargetGroupFilter(value === '' ? null : value)}
                        />
                    </div>
                </div>
                <PackagesTable packages={filteredPackages} onSelect={(packageId) => void navigate(routes.vossPackageDetail(packageId))} />
            </Card.Content>
        </Card>
    );
};

export default PackagesContainer;
